import { useState, useMemo } from "react";
import { Search, MapPin, Phone, X } from "lucide-react";

/*
 * AreasSuburbSearch — Section 3/8 for /service-areas/
 * Navy section — live search across 217+ suburbs with region chip filters.
 * Paste this ENTIRE file into src/pages/ServiceAreas/sections/AreasSuburbSearch.tsx
 * replacing the placeholder content.
 */

const GOLD = "#F5A623";
const NAVY = "#081438";
const NAVY2 = "#0f2347";
const REGIONS: Record<string, string[]> = {
  "Eastern Suburbs": ["Bondi", "Bondi Beach", "Bondi Junction", "Rose Bay", "Double Bay", "Vaucluse", "Watsons Bay", "Woollahra", "Paddington", "Bronte", "Tamarama", "Coogee", "Randwick", "Maroubra", "Kingsford", "Kensington", "Bellevue Hill", "Dover Heights", "North Bondi", "Little Bay"],
  "Western Sydney": ["Parramatta", "Blacktown", "Penrith", "Liverpool", "Fairfield", "Auburn", "Merrylands", "Guildford", "Granville", "Cabramatta", "Wetherill Park", "Seven Hills", "Mount Druitt", "Rooty Hill", "St Marys", "Emu Plains", "Werrington", "Kingswood", "Minchinbury", "Prairiewood", "Smithfield", "Bossley Park", "Bonnyrigg", "Cecil Hills", "Prestons", "Casula", "Campbelltown", "Camden", "Narellan", "Ingleburn", "Leumeah", "Macquarie Fields", "Minto", "Gregory Hills", "Oran Park", "Spring Farm"],
  "North Shore": ["Chatswood", "Lane Cove", "Mosman", "Willoughby", "Neutral Bay", "Cremorne", "Crows Nest", "St Leonards", "Artarmon", "Wollstonecraft", "Naremburn", "Cammeray", "North Sydney", "Kirribilli", "Milsons Point", "Hornsby", "Turramurra", "Wahroonga", "Gordon", "Pymble"],
  "Inner West": ["Marrickville", "Newtown", "Balmain", "Leichhardt", "Ashfield", "Enmore", "Erskineville", "Stanmore", "Petersham", "Dulwich Hill", "Haberfield", "Five Dock", "Drummoyne", "Rozelle", "Annandale"],
  "Northern Beaches": ["Manly", "Dee Why", "Brookvale", "Mona Vale", "Avalon", "Narrabeen", "Warriewood", "Newport", "Freshwater", "Curl Curl", "Collaroy", "Palm Beach", "Bilgola", "Whale Beach", "Bayview"],
  "Hills District": ["Castle Hill", "Baulkham Hills", "Kellyville", "Rouse Hill", "Dural", "Norwest", "Bella Vista", "Glenhaven", "Kenthurst", "Galston", "Cherrybrook", "West Pennant Hills", "Pennant Hills", "Carlingford", "North Rocks"],
  "Sutherland Shire": ["Cronulla", "Miranda", "Caringbah", "Engadine", "Sylvania", "Gymea", "Kirrawee", "Sutherland", "Jannali", "Como", "Woolooware", "Kurnell", "Bundeena", "Menai", "Bangor"],
  "South Sydney": ["Mascot", "Rockdale", "Kogarah", "Hurstville", "Sans Souci", "Bexley", "Arncliffe", "Ramsgate", "Brighton-Le-Sands", "Kyeemagh", "Allawah", "Carlton", "Penshurst", "Mortdale", "Oatley"],
  "Canterbury-Bankstown": ["Bankstown", "Canterbury", "Lakemba", "Punchbowl", "Bass Hill", "Yagoona", "Birrong", "Sefton", "Chester Hill", "Condell Park", "Revesby", "Padstow", "Panania", "East Hills", "Greenacre"],
  "Wollongong": ["Wollongong", "Shellharbour", "Kiama", "Dapto", "Corrimal", "Figtree", "Unanderra", "Fairy Meadow", "Thirroul", "Bulli", "Austinmer", "Helensburgh", "Albion Park", "Barrack Heights", "Warilla"],
  "Central Coast": ["Gosford", "Terrigal", "Erina", "Tuggerah", "Woy Woy", "Umina Beach", "Avoca Beach", "The Entrance", "Kariong", "Wyoming", "Niagara Park", "Lisarow", "Narara", "Point Clare", "Kincumber"],
  "Blue Mountains": ["Katoomba", "Leura", "Springwood", "Blaxland", "Glenbrook", "Hazelbrook", "Wentworth Falls", "Lawson", "Faulconbridge", "Winmalee"]
};
const REGION_NAMES = Object.keys(REGIONS);
const ALL_SUBURBS = Object.values(REGIONS).flat();
export default function AreasSuburbSearch() {
  const [query, setQuery] = useState("");
  const [activeRegion, setRegion] = useState<string | null>(null);
  const filtered = useMemo(() => {
    let pool = activeRegion ? REGIONS[activeRegion] || [] : ALL_SUBURBS;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      pool = pool.filter(s => s.toLowerCase().includes(q));
    }
    return pool;
  }, [query, activeRegion]);
  const displayCount = activeRegion ? filtered.length : ALL_SUBURBS.length;
  return <section id="areas-suburb-search" style={{
    background: NAVY,
    position: "relative",
    overflow: "hidden"
  }}>

      {/* Wave top — DUPLICATE from AreasGrid bottom */}
      <div style={{
      lineHeight: 0
    }}>
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{
        width: "100%",
        display: "block"
      }}>
          <path d="M0,60 C360,0 1080,0 1440,60 L1440,0 L0,0 Z" fill="#FAF7F1" />
        </svg>
      </div>

      {/* Grain */}
      <svg aria-hidden="true" style={{
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      opacity: 0.05,
      pointerEvents: "none",
      zIndex: 1
    }}>
        <filter id="ass-grain"><feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" /><feColorMatrix type="saturate" values="0" /></filter>
        <rect width="100%" height="100%" filter="url(#ass-grain)" />
      </svg>

      {/* Scanlines */}
      <div aria-hidden="true" style={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: `linear-gradient(90deg,transparent,${GOLD}60,transparent)`,
      zIndex: 2
    }} />
      <div aria-hidden="true" style={{
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: `linear-gradient(90deg,transparent,${GOLD}60,transparent)`,
      zIndex: 2
    }} />

      {/* GG watermark */}
      <div aria-hidden="true" style={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%,-50%)",
      fontSize: "280px",
      fontWeight: 900,
      color: `${GOLD}08`,
      fontFamily: "Plus Jakarta Sans,sans-serif",
      pointerEvents: "none",
      zIndex: 1,
      userSelect: "none",
      letterSpacing: "-0.05em"
    }}>
        GG
      </div>

      <div style={{
      position: "relative",
      zIndex: 3,
      maxWidth: "1280px",
      margin: "0 auto",
      padding: "clamp(56px,7vw,88px) clamp(20px,5vw,48px)"
    }}>

        {/* Header */}
        <div style={{
        textAlign: "center",
        marginBottom: "40px"
      }}>
          <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "16px"
        }}>
            <div style={{
            width: "32px",
            height: "2px",
            background: GOLD
          }} />
            <span style={{
            fontFamily: "Manrope,sans-serif",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: `${GOLD}BB`
          }}>
              Search by Suburb
            </span>
            <div style={{
            width: "32px",
            height: "2px",
            background: GOLD
          }} />
          </div>
          <h2 style={{
          fontFamily: "Plus Jakarta Sans,sans-serif",
          fontWeight: 800,
          fontSize: "clamp(26px,3.5vw,42px)",
          lineHeight: 1.1,
          color: "#FFFFFF",
          marginBottom: "12px"
        }}>
            Find Your Suburb — <span style={{
            color: GOLD
          }}>We're There</span>
          </h2>
          <p style={{
          fontFamily: "Manrope,sans-serif",
          fontSize: "clamp(14px,1.5vw,16px)",
          color: "rgba(255,255,255,0.65)",
          lineHeight: 1.7,
          maxWidth: "520px",
          margin: "0 auto"
        }}>
            Type your suburb or filter by region. If it's on the list, we've already fixed doors there.
          </p>
        </div>

        {/* Search bar */}
        <div style={{
        maxWidth: "520px",
        margin: "0 auto 28px",
        position: "relative"
      }}>
          {/** @preserve lucide-do-not-replace */}
          <span data-icon-container="lucide-do-not-replace" style={{
          position: "absolute",
          left: "16px",
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none"
        }}>
            <Search size={18} color={`${GOLD}99`} strokeWidth={2} />
          </span>
          <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search your suburb…" style={{
          width: "100%",
          padding: "14px 44px 14px 46px",
          background: NAVY2,
          border: `1.5px solid rgba(255,255,255,0.12)`,
          borderRadius: "10px",
          fontFamily: "Manrope,sans-serif",
          fontSize: "15px",
          color: "#FFF",
          outline: "none"
        }} />
          {query && <button onClick={() => setQuery("")} style={{
          position: "absolute",
          right: "14px",
          top: "50%",
          transform: "translateY(-50%)",
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "4px",
          lineHeight: 0
        }}>
              {/** @preserve lucide-do-not-replace */}
              <span data-icon-container="lucide-do-not-replace"><X size={16} color="rgba(255,255,255,0.5)" strokeWidth={2} /></span>
            </button>}
        </div>

        {/* Region filter chips */}
        <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
        justifyContent: "center",
        marginBottom: "32px"
      }}>
          <button onClick={() => setRegion(null)} style={{
          padding: "6px 14px",
          borderRadius: "999px",
          border: `1px solid ${!activeRegion ? GOLD : "rgba(255,255,255,0.15)"}`,
          background: !activeRegion ? `${GOLD}20` : "transparent",
          color: !activeRegion ? GOLD : "rgba(255,255,255,0.6)",
          fontFamily: "Manrope,sans-serif",
          fontSize: "12px",
          fontWeight: 600,
          cursor: "pointer"
        }}>
            All ({ALL_SUBURBS.length})
          </button>
          {REGION_NAMES.map(r => {
          const isActive = activeRegion === r;
          return <button key={r} onClick={() => setRegion(isActive ? null : r)} style={{
            padding: "6px 14px",
            borderRadius: "999px",
            border: `1px solid ${isActive ? GOLD : "rgba(255,255,255,0.15)"}`,
            background: isActive ? `${GOLD}20` : "transparent",
            color: isActive ? GOLD : "rgba(255,255,255,0.6)",
            fontFamily: "Manrope,sans-serif",
            fontSize: "12px",
            fontWeight: 600,
            cursor: "pointer",
            whiteSpace: "nowrap"
          }}>
                {r} ({REGIONS[r].length})
              </button>;
        })}
        </div>

        {/* Results count */}
        <p style={{
        fontFamily: "Manrope,sans-serif",
        fontSize: "13px",
        color: "rgba(255,255,255,0.45)",
        textAlign: "center",
        marginBottom: "20px"
      }}>
          {query.trim() ? `${filtered.length} suburb${filtered.length !== 1 ? "s" : ""} found` : `Showing ${displayCount} suburbs${activeRegion ? ` in ${activeRegion}` : ""}`}
        </p>

        {/* Suburb chips */}
        <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "7px",
        justifyContent: "center",
        maxHeight: "320px",
        overflowY: "auto",
        paddingRight: "4px"
      }}>
          {filtered.length > 0 ? filtered.map(s => <span key={s} style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "999px",
          padding: "5px 12px",
          fontFamily: "Manrope,sans-serif",
          fontSize: "11px",
          color: "rgba(255,255,255,0.65)",
          fontWeight: 500
        }}>
              {/** @preserve lucide-do-not-replace */}
              <span data-icon-container="lucide-do-not-replace"><MapPin size={9} color={GOLD} strokeWidth={2} /></span>
              {s}
            </span>) : <div style={{
          textAlign: "center",
          padding: "32px 20px"
        }}>
              <p style={{
            fontFamily: "Manrope,sans-serif",
            fontSize: "15px",
            color: "rgba(255,255,255,0.7)",
            marginBottom: "12px"
          }}>
                Can't find your suburb? We probably still cover it.
              </p>
              <a href="tel:0485000933" style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            color: GOLD,
            fontFamily: "Manrope,sans-serif",
            fontSize: "14px",
            fontWeight: 700,
            textDecoration: "none"
          }}>
                {/** @preserve lucide-do-not-replace */}
                <span data-icon-container="lucide-do-not-replace"><Phone size={14} color={GOLD} strokeWidth={2.5} /></span>
                Call 0485 000 933 to check
              </a>
            </div>}
        </div>

        {/* Bottom note */}
        {filtered.length > 0 && <p style={{
        fontFamily: "Manrope,sans-serif",
        fontSize: "12px",
        color: "rgba(255,255,255,0.35)",
        textAlign: "center",
        marginTop: "20px"
      }}>
            Not listed? Call us — if it's within Greater Sydney, Wollongong or the Central Coast, we're there.
          </p>}
      </div>

      {/* Wave bottom — DUPLICATE in AreasWhyLocal top */}
      <div style={{
      position: "relative",
      zIndex: 3,
      lineHeight: 0
    }}>
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{
        width: "100%",
        display: "block"
      }}>
          <path d="M0,0 C360,60 1080,60 1440,0 L1440,60 L0,60 Z" fill="#FAF7F1" />
        </svg>
      </div>
    </section>;
}